<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import telegramPersonalChannel from 'dashboard/api/channel/telegramPersonalChannel';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  providerConfig: {
    type: Object,
    default: () => ({}),
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['connected', 'disconnected', 'statusChange']);

const { t } = useI18n();
const store = useStore();

const session = ref({
  session_id: props.providerConfig?.session_id || null,
  status: props.providerConfig?.session_status || 'not_started',
  qr_code: props.providerConfig?.session_qr_code || null,
  account_name: props.providerConfig?.connected_account_name || null,
  username: props.providerConfig?.connected_username || null,
  last_error: props.providerConfig?.session_error || null,
  code_delivery: props.providerConfig?.session_code_delivery || null,
});

const code = ref('');
const password = ref('');
const isLoading = ref(false);
const qrCodeDataUrl = ref('');
let pollTimer = null;

const POLLING_STATUSES = [
  'connecting',
  'qr_ready',
  'reconnecting',
  'awaiting_code',
  'awaiting_password',
];

const applySession = nextSession => {
  session.value = {
    session_id: nextSession?.session_id || session.value.session_id,
    status: nextSession?.status || 'not_started',
    qr_code: nextSession?.qr_code || '',
    account_name: nextSession?.account_name || '',
    username: nextSession?.username || '',
    last_error: nextSession?.last_error || '',
    code_delivery: nextSession?.code_delivery || '',
  };
};

const syncInboxes = async () => {
  await store.dispatch('inboxes/get');
};

const updateQrCode = async qrCode => {
  if (!qrCode) {
    qrCodeDataUrl.value = '';
    return;
  }

  qrCodeDataUrl.value = qrCode.startsWith('data:image')
    ? qrCode
    : await QRCode.toDataURL(qrCode);
};

const handleStatusTransition = async previousStatus => {
  if (previousStatus === session.value.status) {
    return;
  }

  const nextStatus = session.value.status;

  if (['connected', 'disconnected'].includes(nextStatus)) {
    await syncInboxes();
  }

  emit('statusChange', session.value);

  if (nextStatus === 'connected') {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CONNECT_SUCCESS'));
    emit('connected', session.value);
  } else if (nextStatus === 'disconnected') {
    emit('disconnected', session.value);
  }
};

const fetchSession = async ({ create = false, silent = false } = {}) => {
  isLoading.value = !silent;

  try {
    const response = create
      ? await telegramPersonalChannel.createPersonalSession({
          inboxId: props.inboxId,
        })
      : await telegramPersonalChannel.getPersonalSession(props.inboxId);

    const previousStatus = session.value.status;
    applySession(response.data.session);
    await handleStatusTransition(previousStatus);
  } catch (error) {
    if (!silent) {
      useAlert(
        error.message ||
          t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.LOAD_ERROR')
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const submitPassword = async () => {
  if (!password.value.trim()) {
    return;
  }

  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await telegramPersonalChannel.submitPersonalPassword({
      inboxId: props.inboxId,
      password: password.value.trim(),
    });
    applySession(response.data.session);
    password.value = '';
    await handleStatusTransition(previousStatus);
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const submitCode = async () => {
  const normalizedCode = code.value.replace(/\s/g, '');
  if (!normalizedCode) {
    return;
  }

  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await telegramPersonalChannel.submitPersonalCode({
      inboxId: props.inboxId,
      code: normalizedCode,
    });
    applySession(response.data.session);
    code.value = '';
    await handleStatusTransition(previousStatus);
    schedulePoll();
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const disconnectSession = async () => {
  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await telegramPersonalChannel.deletePersonalSession({
      inboxId: props.inboxId,
    });
    applySession(response.data.session);
    await handleStatusTransition(previousStatus);
    useAlert(
      t(
        'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.DISCONNECT_SUCCESS'
      )
    );
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const statusLabel = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.STATUS.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusDescription = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.STATUS_HELP.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusClass = computed(() => {
  const variants = {
    connected: 'bg-n-teal-3 text-n-teal-11',
    qr_ready: 'bg-n-amber-3 text-n-amber-11',
    connecting: 'bg-n-blue-3 text-n-blue-11',
    reconnecting: 'bg-n-blue-3 text-n-blue-11',
    awaiting_code: 'bg-n-amber-3 text-n-amber-11',
    awaiting_password: 'bg-n-amber-3 text-n-amber-11',
    disconnected: 'bg-n-slate-3 text-n-slate-11',
    error: 'bg-n-ruby-3 text-n-ruby-11',
    not_started: 'bg-n-slate-3 text-n-slate-11',
  };

  return variants[session.value.status] || variants.not_started;
});

const shouldPoll = computed(() => POLLING_STATUSES.includes(session.value.status));

const primaryActionLabel = computed(() => {
  if (session.value.status === 'connected') {
    return t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.REFRESH_BUTTON');
  }

  if (session.value.status === 'qr_ready') {
    return t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.REFRESH_QR_BUTTON');
  }

  return t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CONNECT_BUTTON');
});

const runPrimaryAction = () => {
  if (session.value.status === 'connected') {
    fetchSession();
    return;
  }

  fetchSession({ create: true });
};

const showPrimaryButton = computed(
  () => !['awaiting_code', 'awaiting_password'].includes(session.value.status)
);

const showDisconnectButton = computed(() => {
  return [
    'connected',
    'connecting',
    'qr_ready',
    'reconnecting',
    'awaiting_code',
    'awaiting_password',
  ].includes(session.value.status);
});

const schedulePoll = () => {
  clearTimeout(pollTimer);
  if (!shouldPoll.value) return;

  pollTimer = window.setTimeout(() => {
    fetchSession({ silent: true });
  }, 3000);
};

watch(
  () => props.providerConfig,
  providerConfig => {
    applySession({
      session_id: providerConfig?.session_id,
      status: providerConfig?.session_status,
      qr_code: providerConfig?.session_qr_code,
      account_name: providerConfig?.connected_account_name,
      username: providerConfig?.connected_username,
      last_error: providerConfig?.session_error,
      code_delivery: providerConfig?.session_code_delivery,
    });
  },
  { deep: true }
);

watch(
  () => session.value.qr_code,
  qrCode => {
    updateQrCode(qrCode);
  },
  { immediate: true }
);

watch(
  () => session.value.status,
  () => {
    schedulePoll();
  },
  { immediate: true }
);

onMounted(() => {
  fetchSession({ silent: true });
});

onUnmounted(() => {
  clearTimeout(pollTimer);
});
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-alpha-2 p-5">
    <div
      class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0 flex-1">
        <div v-if="showTitle">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.SUBHEADER') }}
          </p>
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-2">
          <span
            class="inline-flex rounded-full px-3 py-1 text-xs font-medium"
            :class="statusClass"
          >
            {{ statusLabel }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ statusDescription }}
          </span>
        </div>

        <dl class="mt-4 grid gap-3 text-sm text-n-slate-11 md:grid-cols-2">
          <div>
            <dt class="font-medium text-n-slate-12">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CONNECTED_ACCOUNT')
              }}
            </dt>
            <dd class="mt-1 break-words">
              {{ session.account_name || '—' }}
            </dd>
          </div>
          <div>
            <dt class="font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.USERNAME') }}
            </dt>
            <dd class="mt-1 break-words">
              {{ session.username || '—' }}
            </dd>
          </div>
          <div v-if="session.last_error" class="md:col-span-2">
            <dt class="font-medium text-n-slate-12">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.LAST_ERROR')
              }}
            </dt>
            <dd class="mt-1 break-words text-n-ruby-11">
              {{ session.last_error }}
            </dd>
          </div>
        </dl>

        <form
          v-if="session.status === 'awaiting_code'"
          class="mt-4 flex flex-col gap-3"
          @submit.prevent="submitCode"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CODE_LABEL') }}
            <input
              v-model="code"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9]*"
              maxlength="8"
              class="mt-2 w-full"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CODE_PLACEHOLDER'
                )
              "
              autofocus
            />
          </label>
          <p v-if="session.code_delivery" class="text-xs text-n-slate-11">
            {{
              $t(
                `INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.CODE_DELIVERY.${session.code_delivery.toUpperCase()}`
              )
            }}
          </p>
          <div class="flex justify-start">
            <Button
              type="submit"
              size="sm"
              color="blue"
              :disabled="!code.trim()"
              :is-loading="isLoading"
              :label="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.SUBMIT_CODE_BUTTON'
                )
              "
            />
          </div>
        </form>

        <form
          v-if="session.status === 'awaiting_password'"
          class="mt-4 flex flex-col gap-3"
          @submit.prevent="submitPassword"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.PASSWORD_LABEL')
            }}
            <input
              v-model="password"
              type="password"
              autocomplete="current-password"
              class="mt-2 w-full"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.PASSWORD_PLACEHOLDER'
                )
              "
            />
          </label>
          <div class="flex justify-start">
            <Button
              type="submit"
              size="sm"
              color="blue"
              :disabled="!password.trim()"
              :is-loading="isLoading"
              :label="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.SUBMIT_PASSWORD_BUTTON'
                )
              "
            />
          </div>
        </form>
      </div>

      <div v-if="qrCodeDataUrl" class="mx-auto w-full max-w-[14rem] shrink-0">
        <img :src="qrCodeDataUrl" alt="Telegram personal QR" class="w-full" />
        <p class="mt-3 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.QR_HELP') }}
        </p>
      </div>
    </div>

    <div class="mt-5 flex flex-wrap gap-3">
      <Button
        v-if="showPrimaryButton"
        color="blue"
        :is-loading="isLoading"
        :label="primaryActionLabel"
        @click="runPrimaryAction"
      />
      <Button
        v-if="showDisconnectButton"
        color="slate"
        :is-loading="isLoading"
        :label="
          $t('INBOX_MGMT.SETTINGS_POPUP.TELEGRAM_PERSONAL.DISCONNECT_BUTTON')
        "
        @click="disconnectSession"
      />
    </div>
  </div>
</template>
